import React, { Suspense, useEffect } from 'react';
import { lazyWithRetry, clearChunkRetryFlag } from '../lib/utils/lazyWithRetry';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Footer } from '../components/layout/Footer';
import { Header } from '../components/layout/Header';
import { RouteLoader } from '../components/ui/loader';
import { RouteTransition } from '../components/ui/transitions';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { initializeRoutePreloading } from '../utils/routePreloader';
import { initializeDeviceFingerprinting } from '../lib/utils/deviceFingerprint';
import { AuthProvider } from './AuthContext';
import { useAuth } from './hooks/useAuth';
import { useScrollToTop } from './hooks/useScrollToTop';
import { ToastProvider } from '../shared/ui/toast';
import { AuthOverlayProvider, useAuthOverlay } from './auth/AuthOverlayContext';
import { CaptchaProvider } from '../lib/captcha';
import { StripeProvider } from '../lib/stripe';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { SmartLoginIndicator } from '../components/auth/SmartLoginIndicator';
import { GoogleOneTap } from '../components/auth/GoogleOneTap';
import { VerificationBanner } from '../shared/ui/banners/VerificationBanner';
import AnnouncementBar from '../shared/ui/banners/AnnouncementBar';
import { LiveChatWidget } from '../features/contact/chat/components/LiveChatWidget';
import { UrLatnNastaliqToast } from '../components/ui/UrLatnNastaliqToast';
import { LeaveProtectionProvider } from '../shared/leave-protection/LeaveProtectionProvider';
import { LocaleRedirect, LocaleSync } from '../lib/i18n/LocaleRouter';
import { useLocaleNavigate } from '../lib/i18n/useLocaleNavigate';
import { useCaptureReferralOnLoad } from '../lib/api/ashrafie';
import './globals.css';
import '../utils/accessibility'; // Load accessibility features globally

// Lazy load all pages for better performance and loading states
const HomePage = lazyWithRetry(() => import('../components/pages/HomePage'));
const AboutUsPage = lazyWithRetry(
  () => import('../components/pages/about-us/AboutUsPage')
);
const WhyAmalQExistsPage = lazyWithRetry(
  () => import('../components/pages/why-amalq-exists/WhyAmalQExistsPage')
);
// HowItWorksPage removed -- merged into About Us page
// /how-it-works now redirects to /about-us#how-it-works
const ContactUsPage = lazyWithRetry(
  () => import('../components/pages/contact-us/ContactUsPage')
);
const AboutAIHajraPage = lazyWithRetry(
  () => import('../components/pages/about-ai-hajra/AboutAIHajraPage')
);
const SignUpPage = lazyWithRetry(() => import('../components/pages/sign-up/SignUpPage'));
const LoginPage = lazyWithRetry(() => import('../components/pages/login/LoginPage'));
const TwoFactorVerifyPage = lazyWithRetry(
  () => import('../components/pages/TwoFactorVerifyPage')
);
const VerifyEmailPage = lazyWithRetry(
  () => import('../components/pages/verify-email/VerifyEmailPage')
);
const ForgotPasswordPage = lazyWithRetry(
  () => import('../components/pages/forgot-password/ForgotPasswordPage')
);
const ResetPasswordPage = lazyWithRetry(
  () => import('../components/pages/reset-password/ResetPasswordPage')
);
const BlogListPage = lazyWithRetry(
  () => import('../components/pages/blog/BlogListPage')
);
const BlogDetailPage = lazyWithRetry(
  () => import('../components/pages/blog/BlogDetailPage')
);
const CauseDetailPage = lazyWithRetry(
  () => import('../components/pages/causes/CauseDetailPage')
);
const PrivacyPolicyPage = lazyWithRetry(
  () => import('../components/pages/legal/PrivacyPolicyPage')
);
const TermsOfServicePage = lazyWithRetry(
  () => import('../components/pages/legal/TermsOfServicePage')
);
const CookiePolicyPage = lazyWithRetry(
  () => import('../components/pages/legal/CookiePolicyPage')
);
const IslamicCompliancePage = lazyWithRetry(
  () => import('../components/pages/legal/IslamicCompliancePage')
);
const CommunityGuidelinesPage = lazyWithRetry(
  () => import('../components/pages/legal/CommunityGuidelinesPage')
);
const RefundPolicyPage = lazyWithRetry(
  () => import('../components/pages/legal/RefundPolicyPage')
);
const CampaignTermsPage = lazyWithRetry(
  () => import('../components/pages/legal/CampaignTermsPage')
);
const PaymentTermsPage = lazyWithRetry(
  () => import('../components/pages/legal/PaymentTermsPage')
);
const DataProtectionPage = lazyWithRetry(
  () => import('../components/pages/legal/DataProtectionPage')
);
const HelpCenterPage = lazyWithRetry(
  () => import('../features/help-center/components/HelpCenterPage')
);

// Changelog Pages
const ChangelogListPage = lazyWithRetry(
  () => import('../components/pages/changelog/ChangelogListPage')
);
const ChangelogDetailPage = lazyWithRetry(
  () => import('../components/pages/changelog/ChangelogDetailPage')
);

// Developer Documentation Pages
const HajiQPayDocsPage = lazyWithRetry(
  () => import('../components/pages/developers/HajiQPayDocsPage')
);

// Public Campaign Builder (pre-auth wizard)
const StartCampaignPage = lazyWithRetry(
  () => import('../components/pages/start-campaign/StartCampaignPage')
);
const StartCampaignContinuePage = lazyWithRetry(
  () => import('../components/pages/start-campaign/StartCampaignContinuePage')
);

// Payment return page (GoCardless bank redirect comes back here)
const DonationCompletePage = lazyWithRetry(
  () => import('../features/donation-system/pages/DonationCompletePage')
);

// Crowdfunding (Launchpad) Pages
const LaunchpadDiscoveryPage = lazyWithRetry(
  () => import('../components/pages/launchpad/LaunchpadDiscoveryPage')
);
const ProjectDetailPage = lazyWithRetry(
  () => import('../components/pages/launchpad/ProjectDetailPage')
);

// Crowdfunding Creator Dashboard Pages
const CreatorDashboardPage = lazyWithRetry(
  () => import('../features/crowdfunding/pages/CreatorDashboardPage')
);
const CreateProjectPage = lazyWithRetry(
  () => import('../features/crowdfunding/pages/CreateProjectPage')
);
const EditProjectPage = lazyWithRetry(
  () => import('../features/crowdfunding/pages/EditProjectPage')
);
const ProjectManagementPage = lazyWithRetry(
  () => import('../features/crowdfunding/pages/ProjectManagementPage')
);
const MyBackingsPage = lazyWithRetry(
  () => import('../features/crowdfunding/pages/MyBackingsPage')
);

// Campaign Pages
const CampaignListPage = lazyWithRetry(
  () => import('../components/pages/campaigns/discovery/CampaignDiscoveryPage')
);
const CampaignDetailPage = lazyWithRetry(
  () => import('../components/pages/campaigns/CampaignDetailPage')
);

// Halal Directory Listing Pages
const ListingDiscoveryPage = lazyWithRetry(
  () => import('../features/listings/pages/ListingDiscoveryPage')
);
const ListingMapPage = lazyWithRetry(
  () => import('../features/listings/pages/ListingMapPage')
);
const ListingClaimPage = lazyWithRetry(
  () => import('../features/listings/pages/ListingClaimPage')
);
const ListingDetailPage = lazyWithRetry(
  () => import('../features/listings/pages/ListingDetailPage')
);

// Donation System Pages
const DonationSuccessPage = lazyWithRetry(
  () => import('../features/donation-system/components/SuccessPage')
);
const DonationErrorPage = lazyWithRetry(
  () => import('../features/donation-system/components/ErrorPage')
);
const ZakatCalculatorPage = lazyWithRetry(
  () => import('../features/donation-system/components/ZakatCalculator')
);

// User Dashboard Pages
const TrustedDevicesPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/TrustedDevicesPage')
);

// Wrapper components for donation pages
const DonationSuccessWrapper: React.FC = () => {
  // For now, use a placeholder donation ID - in production this would come from URL params or local storage
  const placeholderDonationId = 'don_' + Date.now();
  return <DonationSuccessPage donationId={placeholderDonationId} />;
};

const DonationErrorWrapper: React.FC = () => {
  // Create a generic error for routing purposes
  const genericError = {
    type: 'system' as const,
    code: 'ROUTE_ERROR',
    message: 'An error occurred while processing your request',
    userMessage: 'Something went wrong. Please try again or contact support.',
    recoverable: true,
    timestamp: Date.now()
  };
  return <DonationErrorPage error={genericError} />;
};

// User Dashboard Pages
const DashboardOverviewPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/DashboardOverviewPage')
);
const MyCampaignsPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/MyCampaignsPage')
);
const UserCampaignDetailPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/CampaignDetailPage')
);
const CampaignEditPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/CampaignEditPage')
);
const DonationsPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/DonationsPage')
);
const GazianOrdersPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/GazianOrdersPage')
);
const GazianOrderDetailPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/GazianOrderDetailPage')
);
const SettingsPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/SettingsPage')
);
const CampaignCreationPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/CampaignCreationPage')
);

// Direct-pay compliance (replaces custodial wallet)
const KycPage = lazyWithRetry(() => import('../features/user-dashboard/pages/KycPage'));
const PayoutDetailsPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/PayoutDetailsPage')
);
const PayPalOnboardingPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/PayPalOnboardingPage')
);
const PayPalOnboardingReturnPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/PayPalOnboardingReturnPage')
);
const PendingDonationsPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/PendingDonationsPage')
);
const EarningsPage = lazyWithRetry(() => import('../features/user-dashboard/pages/EarningsPage'));
const DonationTransferInstructionsPage = lazyWithRetry(
  () => import('../features/donation-system/pages/DonationTransferInstructionsPage')
);
// Ashrafie Rewards Wallet (authenticated)
const AshrafiePage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/AshrafiePage')
);
const AshrafieTransactionsPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/AshrafieTransactionsPage')
);
const AshrafieRedeemPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/AshrafieRedeemPage')
);
const AshrafiePassportPage = lazyWithRetry(
  () => import('../features/user-dashboard/pages/AshrafiePassportPage')
);
// Fundasister Fellowship -- Shariah-compliant women entrepreneur funding
const FundasisterLandingPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterLandingPage')
);
const FundasisterTransparencyPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterTransparencyPage')
);
const FundasisterDirectoryPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterDirectoryPage')
);
const FundasisterVentureProfilePage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterVentureProfilePage')
);
const FundasisterApplyPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterApplyPage')
);
const FundasisterMyApplicationPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterMyApplicationPage')
);
const FundasisterVentureDashboardPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterVentureDashboardPage')
);
const FundasisterContributePage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterContributePage')
);
const FundasisterMyDonationsPage = lazyWithRetry(
  () => import('../features/fundasister/pages/FundasisterMyDonationsPage')
);

// Admin Panel - Isolated App
const AdminApp = lazyWithRetry(() => import('../features/admin-dashboard/AdminApp'));

// Gazian Water
const GazianLandingPage = lazyWithRetry(
  () => import('../components/pages/gazian-water/GazianLandingPage')
);
const GazianWaterProductPage = lazyWithRetry(
  () => import('../components/pages/gazian-water/GazianWaterProductPage')
);
const GazianOrderConfirmationPage = lazyWithRetry(
  () => import('../components/pages/gazian-water/GazianOrderConfirmationPage')
);
const GazianOrderTrackingPage = lazyWithRetry(
  () => import('../components/pages/gazian-water/GazianOrderTrackingPage')
);

// Bangrilla Community Hospital
const BangrillaLandingPage = lazyWithRetry(
  () => import('../components/pages/bangrilla/BangrillaLandingPage')
);

// Ashrafie Rewards
const AshrafieLandingPage = lazyWithRetry(
  () => import('../components/pages/ashrafie/AshrafieLandingPage')
);
const AshrafieTermsPage = lazyWithRetry(
  () => import('../components/pages/ashrafie/AshrafieTermsPage')
);
const AshrafiePublicPassportPage = lazyWithRetry(
  () => import('../components/pages/ashrafie/AshrafiePublicPassportPage')
);

/**
 * Route Error Fallback Component
 * Provides user-friendly error UI when a route fails to render
 */
const RouteErrorFallback: React.FC<{
  error?: Error;
  resetError?: () => void;
}> = ({ error, resetError }) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-white to-emerald-50/30">
      <div className="mx-auto max-w-md px-6 py-12 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <svg
            className="h-8 w-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h1 className="mb-4 text-2xl font-bold text-gray-900">
          {t('errors.routeError.title')}
        </h1>
        <p className="mb-8 text-gray-600">
          {t('errors.routeError.description')}
        </p>
        {error && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              {t('errors.routeError.technicalDetails')}
            </summary>
            <pre className="mt-2 overflow-auto rounded-sm bg-gray-100 p-3 text-xs">
              {error.message}
            </pre>
          </details>
        )}
        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full rounded-lg bg-emerald-600 px-6 py-3 font-medium text-white transition-colors duration-200 hover:bg-emerald-700"
          >
            {t('actions.refreshPage')}
          </button>
          {resetError && (
            <button
              onClick={resetError}
              className="w-full rounded-lg bg-gray-200 px-6 py-3 font-medium text-gray-800 transition-colors duration-200 hover:bg-gray-300"
            >
              {t('actions.tryAgain')}
            </button>
          )}
          <button
            onClick={() => (window.location.href = '/')}
            className="w-full rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
          >
            {t('actions.goHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Safe Route Wrapper Component
 * Wraps routes with error boundaries to prevent blank pages
 * Uses location-based key to reset error boundary on route changes
 */
const SafeRoute: React.FC<{ children: React.ReactNode; routeName: string }> = ({
  children,
  routeName
}) => {
  const location = useLocation();

  return (
    <ErrorBoundary
      key={location.pathname} // Force remount on route change to reset error state
      fallbackRender={({ error, resetError }) => (
        <RouteErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error, errorInfo) => {
        console.error(
          `Route "${routeName}" failed to render:`,
          error,
          errorInfo
        );
        // Could send to error reporting service here
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * App Content Component with Authentication
 * Handles routing and authentication state
 */
const AppContent: React.FC = () => {
  const { authState, logout } = useAuth();
  const { t } = useTranslation('auth');
  const localeNavigate = useLocaleNavigate();
  const location = useLocation();
  const { openAuthSheet } = useAuthOverlay();

  // Scroll to top on route changes (fixes SPA navigation scroll issue)
  useScrollToTop();

  // Initialize route preloading on app start
  useEffect(() => {
    // Initialize global app features
    initializeRoutePreloading().catch(error => {
      console.warn(
        'Route preloading initialization failed, but app will continue working:',
        error
      );
    });
  }, []);

  // Debug: Log auth state changes
  useEffect(() => {}, [authState]);

  // Authentication handlers -- use locale-aware navigation
  const handleLogin = (): void => {
    openAuthSheet({
      returnTo: location.pathname + location.search,
      reason: t('authSheet.signInToContinue', 'Sign in to continue')
    });
  };

  const handleSignup = (): void => {
    openAuthSheet({
      returnTo: location.pathname + location.search,
      reason: t('authSheet.createAccount', 'Create your account')
    });
  };

  const handleLogout = (): void => {
    // Call the logout function from AuthContext to clear auth state
    logout();

    // Small delay to ensure state update is processed before navigation
    setTimeout(() => {
      localeNavigate('/');
    }, 100);
  };

  // Check if current route is admin route - reactive check using location
  const isAdminRoute = location.pathname.startsWith('/admin');
  // Isolated experience: partner pages that render their own header/footer
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const ISOLATED_EXPERIENCE_SEGMENTS = ['gazian', 'bangrilla', 'ashrafie'] as const;
  // Match ONLY the page segment (right after the locale prefix), e.g.
  // /:locale/ashrafie. Nested app routes like /:locale/dashboard/ashrafie must
  // keep the global header/footer. A plain .some() wrongly isolated any path
  // that merely contained one of these words, which stripped the header from
  // the Ashrafie dashboard pages.
  const isolatedList = ISOLATED_EXPERIENCE_SEGMENTS as ReadonlyArray<string>;
  const isIsolatedExperience =
    isolatedList.includes(pathSegments[1]) ||
    (pathSegments.length === 1 && isolatedList.includes(pathSegments[0]));

  // If admin route, render only AdminApp without layout
  if (isAdminRoute) {
    return (
      <Suspense fallback={<RouteLoader isLoading={true} />}>
        <AdminApp />
      </Suspense>
    );
  }

  // Otherwise, render normal app with layout
  // Rendering normal app layout
  return (
    <div
      className={`flex min-h-screen w-full flex-col overflow-x-hidden ${
        'bg-white dark:bg-gray-900'
      }`}
    >
      {!isIsolatedExperience && (
        <>
          {/* Announcement Bar -- thin promo banner, landing page only */}
          <AnnouncementBar />

          {/* Header Navigation - Present on standard pages */}
          <Header
            authState={authState}
            onLogin={handleLogin}
            onSignup={handleSignup}
            onLogout={handleLogout}
          />

          {/* Smart Login Indicator */}
          <SmartLoginIndicator />

          {/* Google One Tap -- floating account picker for unauthenticated users */}
          <GoogleOneTap />

          {/* Email verification banner — global, cross-cutting (every authenticated
              action eventually needs a verified email). Identity / KYC reminder
              is dashboard-scoped and lives in UserDashboardLayout. */}
          <VerificationBanner />
        </>
      )}

      {/* Main Content - Changes based on route */}
      <main className="flex-1">
        <RouteTransition disableTransitions={true}>
          <Suspense fallback={<RouteLoader isLoading={true} />}>
            <Routes>
              {/* Root redirect -- detects browser language and redirects to /:locale */}
              <Route path="/" element={<LocaleRedirect />} />

              {/* Locale-prefixed routes -- all app routes live under /:locale */}
              <Route path="/:locale" element={<LocaleSync />}>
                {/* Home Page Route */}
                <Route
                  index
                  element={
                    <SafeRoute routeName="Home">
                      <HomePage />
                    </SafeRoute>
                  }
                />

                {/* About Pages */}
                <Route
                  path="about-us"
                  element={
                    <SafeRoute routeName="About Us">
                      <AboutUsPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="why-amalq-exists"
                  element={
                    <SafeRoute routeName="Why AmalQ Exists">
                      <WhyAmalQExistsPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="about-ask-hajra"
                  element={
                    <SafeRoute routeName="About Ask Hajra">
                      <AboutAIHajraPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="about-ask"
                  element={<Navigate to="../about-ask-hajra" replace />}
                />
                <Route
                  path="about-ai-hajra"
                  element={<Navigate to="../about-ask-hajra" replace />}
                />
                <Route
                  path="how-it-works"
                  element={<Navigate to="../about-us#how-it-works" replace />}
                />

                {/* Blog Routes */}
                <Route
                  path="blog"
                  element={
                    <SafeRoute routeName="Blog List">
                      <BlogListPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="blog/:slug"
                  element={
                    <SafeRoute routeName="Blog Detail">
                      <BlogDetailPage />
                    </SafeRoute>
                  }
                />

                {/* Changelog Routes */}
                <Route
                  path="changelog"
                  element={
                    <SafeRoute routeName="Changelog">
                      <ChangelogListPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="changelog/:slug"
                  element={
                    <SafeRoute routeName="Changelog Detail">
                      <ChangelogDetailPage />
                    </SafeRoute>
                  }
                />

                {/* Contact */}
                <Route
                  path="contact"
                  element={
                    <SafeRoute routeName="Contact">
                      <ContactUsPage />
                    </SafeRoute>
                  }
                />

                {/* Auth Routes */}
                <Route
                  path="register"
                  element={
                    <SafeRoute routeName="Sign Up">
                      <SignUpPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="login"
                  element={
                    <SafeRoute routeName="Login">
                      <LoginPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="verify-2fa"
                  element={
                    <SafeRoute routeName="2FA Verification">
                      <TwoFactorVerifyPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="verify-email"
                  element={
                    <ProtectedRoute>
                      <SafeRoute routeName="Email Verification">
                        <VerifyEmailPage />
                      </SafeRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="forgot-password"
                  element={
                    <SafeRoute routeName="Forgot Password">
                      <ForgotPasswordPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="reset-password"
                  element={
                    <SafeRoute routeName="Reset Password">
                      <ResetPasswordPage />
                    </SafeRoute>
                  }
                />

                {/* Legal Pages */}
                <Route
                  path="privacy"
                  element={
                    <SafeRoute routeName="Privacy Policy">
                      <PrivacyPolicyPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="terms"
                  element={
                    <SafeRoute routeName="Terms of Service">
                      <TermsOfServicePage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="cookies"
                  element={
                    <SafeRoute routeName="Cookie Policy">
                      <CookiePolicyPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="islamic-compliance"
                  element={
                    <SafeRoute routeName="Islamic Compliance">
                      <IslamicCompliancePage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="community-guidelines"
                  element={
                    <SafeRoute routeName="Community Guidelines">
                      <CommunityGuidelinesPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="refund-policy"
                  element={
                    <SafeRoute routeName="Refund Policy">
                      <RefundPolicyPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="campaign-terms"
                  element={
                    <SafeRoute routeName="Campaign Terms">
                      <CampaignTermsPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="payment-terms"
                  element={
                    <SafeRoute routeName="Payment Terms">
                      <PaymentTermsPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="data-protection"
                  element={
                    <SafeRoute routeName="Data Protection">
                      <DataProtectionPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="help"
                  element={
                    <SafeRoute routeName="Help Center">
                      <HelpCenterPage />
                    </SafeRoute>
                  }
                />

                {/* Developer Documentation */}
                <Route
                  path="developers/hajiq-pay"
                  element={
                    <SafeRoute routeName="HajiPay Docs">
                      <HajiQPayDocsPage />
                    </SafeRoute>
                  }
                />

                {/* Public Campaign Builder (pre-auth wizard) */}
                <Route
                  path="start-campaign"
                  element={
                    <SafeRoute routeName="Start Campaign">
                      <StartCampaignPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="start-campaign/continue"
                  element={
                    <SafeRoute routeName="Continue Campaign">
                      <ProtectedRoute>
                        <StartCampaignContinuePage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Crowdfunding (Launchpad) Routes */}
                <Route
                  path="launchpad"
                  element={
                    <SafeRoute routeName="Launchpad">
                      <LaunchpadDiscoveryPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="launchpad/:slug"
                  element={
                    <SafeRoute routeName="Project Detail">
                      <ProjectDetailPage />
                    </SafeRoute>
                  }
                />

                {/* Curated Cause Routes */}
                <Route
                  path="causes/:slug"
                  element={
                    <SafeRoute routeName="Cause Detail">
                      <CauseDetailPage />
                    </SafeRoute>
                  }
                />

                {/* Campaign Routes */}
                <Route
                  path="campaigns"
                  element={
                    <SafeRoute routeName="Campaigns">
                      <CampaignListPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="campaigns/:slug"
                  element={
                    <SafeRoute routeName="Campaign Detail">
                      <CampaignDetailPage />
                    </SafeRoute>
                  }
                />

                {/* Halal Directory Routes -- discovery ends in contacting a
                    business directly. No money flows through these pages. */}
                <Route
                  path="listings"
                  element={
                    <SafeRoute routeName="Listings">
                      <ListingDiscoveryPage />
                    </SafeRoute>
                  }
                />
                {/* Map view — registered ABOVE listings/:slug so "map" is matched
                    as this literal route, never bound as a listing slug. */}
                <Route
                  path="listings/map"
                  element={
                    <SafeRoute routeName="Listings Map">
                      <ListingMapPage />
                    </SafeRoute>
                  }
                />
                {/* Claim flow — registered ABOVE listings/:slug so "claim" is matched
                    as this nested route, never captured as part of a slug. */}
                <Route
                  path="listings/:slug/claim"
                  element={
                    <SafeRoute routeName="Listing Claim">
                      <ListingClaimPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="listings/:slug"
                  element={
                    <SafeRoute routeName="Listing Detail">
                      <ListingDetailPage />
                    </SafeRoute>
                  }
                />

                {/* Donation Complete (GoCardless bank redirect return) */}
                <Route
                  path="donation-complete"
                  element={
                    <SafeRoute routeName="Donation Complete">
                      <DonationCompletePage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="donation/transfer/:intentKey"
                  element={
                    <SafeRoute routeName="Bank transfer instructions">
                      <DonationTransferInstructionsPage />
                    </SafeRoute>
                  }
                />

                {/* Gazian Water */}
                <Route
                  path="gazian"
                  element={
                    <SafeRoute routeName="Gazian Landing">
                      <GazianLandingPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="gazian/water"
                  element={
                    <SafeRoute routeName="Gazian Water Product">
                      <GazianWaterProductPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="gazian-water/order/:orderNumber"
                  element={
                    <SafeRoute routeName="Gazian Order Confirmation">
                      <GazianOrderConfirmationPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="gazian-water/track/:orderNumber?"
                  element={
                    <SafeRoute routeName="Gazian Order Tracking">
                      <GazianOrderTrackingPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="water"
                  element={<Navigate to="../gazian/water" replace />}
                />
                <Route
                  path="gazian-water"
                  element={<Navigate to="../gazian" replace />}
                />

                {/* Bangrilla Community Hospital */}
                <Route
                  path="bangrilla"
                  element={
                    <SafeRoute routeName="Bangrilla Hospital">
                      <BangrillaLandingPage />
                    </SafeRoute>
                  }
                />

                {/* Ashrafie Rewards */}
                <Route
                  path="ashrafie"
                  element={
                    <SafeRoute routeName="Ashrafie Rewards">
                      <AshrafieLandingPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="ashrafie/terms"
                  element={
                    <SafeRoute routeName="Ashrafie Terms">
                      <AshrafieTermsPage />
                    </SafeRoute>
                  }
                />
                {/* Public shareable Ashrafie passport — NO auth (filtered DTO / 404) */}
                <Route
                  path="passport/:handle"
                  element={
                    <SafeRoute routeName="Ashrafie Public Passport">
                      <AshrafiePublicPassportPage />
                    </SafeRoute>
                  }
                />

                {/* Legacy URL Redirects */}
                <Route
                  path="impact"
                  element={<Navigate to="../campaigns" replace />}
                />
                <Route
                  path="join"
                  element={<Navigate to="../register" replace />}
                />
                <Route
                  path="get-started"
                  element={<Navigate to="../register" replace />}
                />
                <Route
                  path="about"
                  element={<Navigate to="../about-us" replace />}
                />

                {/* Donation System Routes */}
                <Route
                  path="donate/success"
                  element={
                    <SafeRoute routeName="Donation Success">
                      <DonationSuccessWrapper />
                    </SafeRoute>
                  }
                />
                <Route
                  path="donate/error"
                  element={
                    <SafeRoute routeName="Donation Error">
                      <DonationErrorWrapper />
                    </SafeRoute>
                  }
                />
                <Route
                  path="zakat-calculator"
                  element={
                    <SafeRoute routeName="Zakat Calculator">
                      <ZakatCalculatorPage />
                    </SafeRoute>
                  }
                />

                {/* User Dashboard Routes - PROTECTED */}
                <Route
                  path="dashboard"
                  element={
                    <SafeRoute routeName="Dashboard">
                      <ProtectedRoute>
                        <DashboardOverviewPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/my-campaigns"
                  element={
                    <SafeRoute routeName="My Campaigns">
                      <ProtectedRoute>
                        <MyCampaignsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/campaigns/:id"
                  element={
                    <SafeRoute routeName="Campaign Details">
                      <ProtectedRoute>
                        <UserCampaignDetailPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/campaigns/:id/edit"
                  element={
                    <SafeRoute routeName="Edit Campaign">
                      <ProtectedRoute>
                        <CampaignEditPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/campaigns/:id/edit/:step"
                  element={
                    <SafeRoute routeName="Edit Campaign Step">
                      <ProtectedRoute>
                        <CampaignEditPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/donations"
                  element={
                    <SafeRoute routeName="My Donations">
                      <ProtectedRoute>
                        <DonationsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/orders"
                  element={
                    <SafeRoute routeName="My Orders">
                      <ProtectedRoute>
                        <GazianOrdersPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/orders/:orderNumber"
                  element={
                    <SafeRoute routeName="Order Details">
                      <ProtectedRoute>
                        <GazianOrderDetailPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/profile"
                  element={
                    <SafeRoute routeName="Settings">
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/trusted-devices"
                  element={
                    <SafeRoute routeName="Trusted Devices">
                      <ProtectedRoute>
                        <TrustedDevicesPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Direct-pay compliance */}
                <Route
                  path="dashboard/kyc"
                  element={
                    <SafeRoute routeName="KYC">
                      <ProtectedRoute>
                        <KycPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/payout-details"
                  element={
                    <SafeRoute routeName="Payout details">
                      <ProtectedRoute>
                        <PayoutDetailsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/paypal-onboarding"
                  element={
                    <SafeRoute routeName="PayPal onboarding">
                      <ProtectedRoute>
                        <PayPalOnboardingPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/paypal-onboarding/return"
                  element={
                    <SafeRoute routeName="PayPal onboarding return">
                      <ProtectedRoute>
                        <PayPalOnboardingReturnPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/pending-donations"
                  element={
                    <SafeRoute routeName="Pending donations">
                      <ProtectedRoute>
                        <PendingDonationsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/earnings"
                  element={
                    <SafeRoute routeName="Earnings">
                      <ProtectedRoute>
                        <EarningsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Ashrafie Rewards Wallet */}
                <Route
                  path="dashboard/ashrafie"
                  element={
                    <SafeRoute routeName="Ashrafie">
                      <ProtectedRoute>
                        <AshrafiePage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/ashrafie/transactions"
                  element={
                    <SafeRoute routeName="Ashrafie Transactions">
                      <ProtectedRoute>
                        <AshrafieTransactionsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/ashrafie/redeem"
                  element={
                    <SafeRoute routeName="Redeem Ashrafies">
                      <ProtectedRoute>
                        <AshrafieRedeemPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/ashrafie/passport"
                  element={
                    <SafeRoute routeName="Ashrafie Passport">
                      <ProtectedRoute>
                        <AshrafiePassportPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Campaign Creation */}
                <Route
                  path="dashboard/create-campaign"
                  element={
                    <SafeRoute routeName="Create Campaign">
                      <ProtectedRoute>
                        <Navigate to="title" replace />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/create-campaign/:step"
                  element={
                    <SafeRoute routeName="Create Campaign">
                      <ProtectedRoute>
                        <CampaignCreationPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Crowdfunding Creator Dashboard - PROTECTED */}
                <Route
                  path="dashboard/launchpad"
                  element={
                    <SafeRoute routeName="My Launchpad">
                      <ProtectedRoute>
                        <CreatorDashboardPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/launchpad/create"
                  element={
                    <SafeRoute routeName="Create Project">
                      <ProtectedRoute>
                        <Navigate to="basics" replace />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/launchpad/create/:step"
                  element={
                    <SafeRoute routeName="Create Project">
                      <ProtectedRoute>
                        <CreateProjectPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/launchpad/backings"
                  element={
                    <SafeRoute routeName="My Backings">
                      <ProtectedRoute>
                        <MyBackingsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/launchpad/:id/manage"
                  element={
                    <SafeRoute routeName="Manage Project">
                      <ProtectedRoute>
                        <ProjectManagementPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/launchpad/:id/edit"
                  element={
                    <SafeRoute routeName="Edit Project">
                      <ProtectedRoute>
                        <EditProjectPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="dashboard/launchpad/:id/edit/:step"
                  element={
                    <SafeRoute routeName="Edit Project Step">
                      <ProtectedRoute>
                        <EditProjectPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Fundasister Fellowship -- Shariah-compliant women entrepreneur funding */}
                {/* Public routes (no auth required) */}
                <Route
                  path="fundasister"
                  element={
                    <SafeRoute routeName="Fundasister Fellowship">
                      <FundasisterLandingPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/transparency"
                  element={
                    <SafeRoute routeName="Fellowship Transparency">
                      <FundasisterTransparencyPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/directory"
                  element={
                    <SafeRoute routeName="Fellowship Directory">
                      <FundasisterDirectoryPage />
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/ventures/:id"
                  element={
                    <SafeRoute routeName="Fellowship Venture Profile">
                      <FundasisterVentureProfilePage />
                    </SafeRoute>
                  }
                />
                {/* Authenticated user routes */}
                <Route
                  path="fundasister/apply"
                  element={
                    <SafeRoute routeName="Fellowship Apply">
                      <ProtectedRoute>
                        <FundasisterApplyPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/my-application"
                  element={
                    <SafeRoute routeName="My Application">
                      <ProtectedRoute>
                        <FundasisterMyApplicationPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/my-venture"
                  element={
                    <SafeRoute routeName="My Venture">
                      <ProtectedRoute>
                        <FundasisterVentureDashboardPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/contribute"
                  element={
                    <SafeRoute routeName="Fellowship Contribute">
                      <ProtectedRoute>
                        <FundasisterContributePage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />
                <Route
                  path="fundasister/my-donations"
                  element={
                    <SafeRoute routeName="My Donations">
                      <ProtectedRoute>
                        <FundasisterMyDonationsPage />
                      </ProtectedRoute>
                    </SafeRoute>
                  }
                />

                {/* Fallback route within locale - show home */}
                <Route
                  path="*"
                  element={
                    <SafeRoute routeName="404 - Home">
                      <HomePage />
                    </SafeRoute>
                  }
                />
              </Route>

              {/* Catch-all: redirect bare paths (without locale prefix) to locale-prefixed version */}
              <Route path="*" element={<LocaleRedirect />} />
            </Routes>
          </Suspense>
        </RouteTransition>
      </main>

      {!isIsolatedExperience && (
        <>
          {/* Footer - Present on standard pages - sticks to bottom */}
          <Footer />
        </>
      )}
    </div>
  );
};

/**
 * Create QueryClient instance for React Query
 * Configured for optimal performance and caching
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount: number, error: unknown): boolean => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      refetchOnWindowFocus: false, // Disable auto-refetch on window focus
      refetchOnReconnect: true // Refetch when connection is restored
    },
    mutations: {
      retry: false // Don't retry mutations by default
    }
  }
});

/**
 * Main App component for HajiQube Frontend
 * Serves as the root component with routing and layout structure
 */
export const App: React.FC = () => {
  // Capture a `?ref=` referral code from the landing URL once, before the auth
  // overlay flow or any redirect can strip it. Persisted for post-register use.
  useCaptureReferralOnLoad();

  // Initialize device fingerprinting early in app lifecycle
  // Clear chunk retry flag on successful load (enables retry on next deploy)
  React.useEffect(() => {
    try {
      initializeDeviceFingerprinting();
    } catch {
      // Silently fail -- fingerprinting is non-critical
    }
    clearChunkRetryFlag();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <StripeProvider>
        <CaptchaProvider>
          <AuthProvider>
            <ToastProvider position="top-right" maxToasts={3}>
              <Router>
                <ErrorBoundary
                  onError={(error, errorInfo) => {
                    console.error('AppContent error:', error, errorInfo);

                    // HMR context errors in development -- auto-recover
                    if (
                      process.env.NODE_ENV === 'development' &&
                      error.message.includes('must be used within')
                    ) {
                      setTimeout(() => {
                        window.location.reload();
                      }, 1000);
                    }
                  }}
                >
                  <AuthOverlayProvider>
                    <LeaveProtectionProvider>
                      <AppContent />
                    </LeaveProtectionProvider>
                    {/* Global Live Chat Widget - inside AuthOverlayProvider so ChatAuthGate can open the auth sheet */}
                    <ErrorBoundary
                      fallback={null}
                      onError={error => {
                        console.error('Chat widget error:', error);
                      }}
                    >
                      <LiveChatWidget chatbotName="Ask Hajra" />
                    </ErrorBoundary>
                    <UrLatnNastaliqToast />
                  </AuthOverlayProvider>
                </ErrorBoundary>
              </Router>
            </ToastProvider>
          </AuthProvider>
        </CaptchaProvider>
      </StripeProvider>
    </QueryClientProvider>
  );
};
